import { MouseEventHandler, useCallback } from "react";
import { useAssetDelete } from "domains/assets/hooks/useAssetDelete";
import { useAssetDownload } from "domains/assets/hooks/useAssetDownload";
import {
  useAssetCanvasLink,
  useAssetConvertTo3DLink,
  useAssetConvertToVideoLink,
  useAssetExpandLink,
  useAssetPromptEditLink,
  useAssetReloadParametersLink,
  useAssetSearchSimilar,
  useAssetUpscaleLink,
} from "domains/assets/hooks/useAssetLink";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import { ButtonProps, IconButton } from "domains/ui/components/Button";
import Icon from "domains/ui/components/Icon";
import MenuItem from "domains/ui/components/Menu/MenuItem";
import MenuTitle from "domains/ui/components/Menu/MenuTitle";
import { useUserContext } from "domains/user/contexts/UserProvider";
import { AnalyticsEvents } from "infra/analytics/constants/Events";
import Track from "infra/analytics/Track";
import { GetAssetsByAssetIdApiResponse } from "infra/api/generated/api";

import {
  Menu,
  MenuButton,
  MenuDivider,
  MenuList,
  MenuProps,
  Portal,
  useDisclosure,
} from "@chakra-ui/react";

import { ActionType, FmImageCardActionsProps } from "./Actions";

type MoreButtonProps<C extends object = object> = ButtonProps & {
  asset: GetAssetsByAssetIdApiResponse["asset"] | undefined;
  containerRef?: React.RefObject<HTMLDivElement>;
  isHidden?: boolean;
  onToggle?: (isOpen: boolean) => void;
  onActionClick?: (type: ActionType) => void;

  actionsProps?: C;
  ActionsComponent?: React.FunctionComponent<FmImageCardActionsProps<C>>;

  menuProps?: Partial<MenuProps>;
};

export default function MoreButton<C extends object = object>({
  asset,
  containerRef,
  isHidden = false,
  onToggle,
  onActionClick,

  actionsProps = {} as C,
  ActionsComponent,

  menuProps,
  ...props
}: MoreButtonProps<C>) {
  const { isReader } = useTeamContext();
  const { isUnsigned } = useUserContext();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { handleDownload, isDownloading } = useAssetDownload({
    asset,
    trackingEvent: AnalyticsEvents.CardAction.ClickedDownload,
  });
  const { handleDelete, isDeleting, canDelete } = useAssetDelete({
    asset,
    trackingEvent: AnalyticsEvents.CardAction.ClickedDelete,
  });

  const linkReloadAllParameters = useAssetReloadParametersLink(
    isOpen ? asset : undefined
  );
  const linkUpscale = useAssetUpscaleLink(asset);
  const linkCanvas = useAssetCanvasLink(asset);
  const linkExpand = useAssetExpandLink(asset);
  const linkPromptEdit = useAssetPromptEditLink(asset);
  const linkConvertTo3D = useAssetConvertTo3DLink(asset);
  const linkConvertToVideo = useAssetConvertToVideoLink(asset);
  const linkSearch = useAssetSearchSimilar(asset);

  const isReloadAllParametersAvailable = !!linkReloadAllParameters && !isReader;
  const isEditSectionAvailable =
    (linkUpscale || linkCanvas || linkExpand || linkPromptEdit) && !isReader;
  const isConvertSectionAvailable =
    (linkConvertTo3D || linkConvertToVideo) && !isReader;

  // ----------------------------------

  const open = useCallback(() => {
    onOpen();
    onToggle?.(true);
  }, [onOpen, onToggle]);

  const close = useCallback(() => {
    onClose();
    onToggle?.(false);
  }, [onClose, onToggle]);

  // ----------------------------------

  const handleButtonClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      isOpen ? close() : open();
    },
    [close, open, isOpen]
  );

  const handleUpscaleClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      e.stopPropagation();
      Track(AnalyticsEvents.CardAction.ClickedUpscale);
      if (onActionClick) onActionClick("upscale");
    },
    [onActionClick]
  );

  const handleRetouchClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      e.stopPropagation();
      Track(AnalyticsEvents.CardAction.ClickedRetouch);
      if (onActionClick) onActionClick("retouch");
    },
    [onActionClick]
  );

  const handleExpandClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      e.stopPropagation();
      Track(AnalyticsEvents.CardAction.ClickedExpand);
      if (onActionClick) onActionClick("expand");
    },
    [onActionClick]
  );

  const handlePromptEditClick = useCallback<
    MouseEventHandler<HTMLButtonElement>
  >(
    (e) => {
      e.stopPropagation();
      Track(AnalyticsEvents.CardAction.ClickedPromptEdit);
      if (onActionClick) onActionClick("promptEdit");
    },
    [onActionClick]
  );

  const handleMoreClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      Track(AnalyticsEvents.CardAction.ClickedMore);
      if (onActionClick) onActionClick("more");
    },
    [onActionClick]
  );

  const handleConvertTo3DClick = useCallback<
    MouseEventHandler<HTMLButtonElement>
  >(
    (e) => {
      e.stopPropagation();
      Track(AnalyticsEvents.CardAction.ClickedConvertTo3D);
      if (onActionClick) onActionClick("convertTo3D");
    },
    [onActionClick]
  );

  const handleConvertToVideoClick = useCallback<
    MouseEventHandler<HTMLButtonElement>
  >(
    (e) => {
      e.stopPropagation();
      Track(AnalyticsEvents.CardAction.ClickedConvertToVideo);
      if (onActionClick) onActionClick("convertToVideo");
    },
    [onActionClick]
  );

  const handleSearchSimilarClick = useCallback<
    MouseEventHandler<HTMLButtonElement>
  >(
    (e) => {
      e.stopPropagation();
      Track(AnalyticsEvents.CardAction.ClickedSearchSimilar);
      if (onActionClick) onActionClick("searchSimilar");
    },
    [onActionClick]
  );

  const handleDownloadClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    async (e) => {
      e.stopPropagation();
      await handleDownload("png");
      if (onActionClick) onActionClick("download");
    },
    [handleDownload, onActionClick]
  );

  const handleDeleteClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    async (e) => {
      e.stopPropagation();
      await handleDelete();
      if (onActionClick) onActionClick("delete");
    },
    [handleDelete, onActionClick]
  );

  // ----------------------------------

  if (isUnsigned) return null;

  return (
    <Menu
      computePositionOnMount
      eventListeners={{ scroll: false }}
      isLazy
      placement="right-start"
      variant="blurred"
      {...menuProps}
      isOpen={isOpen}
      onClose={close}
    >
      <MenuButton
        as={IconButton}
        borderRadius="lg"
        aria-label="More"
        icon={<Icon id="Ui/Dots" h="3.5px" pointerEvents="none" />}
        size="sm"
        variant="blurred"
        {...props}
        {...(!isHidden || isOpen
          ? { visibility: "visible" }
          : { visibility: "hidden" })}
        onClick={handleButtonClick}
      />

      <Portal containerRef={containerRef}>
        <MenuList
          zIndex="popover"
          maxH={700}
          data-outside-click-excluded
          onClick={(e) => e.stopPropagation()}
        >
          {isReloadAllParametersAvailable && (
            <>
              <MenuTitle variant="blurred" text="USE" />
              <MenuItem
                w="170px"
                iconId="Ui/RefreshAlt"
                iconH="12px"
                internalLink={linkReloadAllParameters?.link}
                isLoading={linkReloadAllParameters?.isLoading}
                onClick={(e) => {
                  e.stopPropagation();
                  Track(AnalyticsEvents.CardAction.ReloadedParameters);
                  if (onActionClick) onActionClick("reloadParameters");
                }}
                text="All Parameters"
              />
            </>
          )}

          {ActionsComponent && !isReader && (
            <ActionsComponent
              {...actionsProps}
              asset={asset}
              top={
                !isReloadAllParametersAvailable ? (
                  <MenuTitle variant="blurred" text="USE" />
                ) : undefined
              }
              onActionClick={onActionClick}
              bottom={
                !isReloadAllParametersAvailable ? <MenuDivider /> : undefined
              }
            />
          )}

          {isReloadAllParametersAvailable && <MenuDivider />}

          {isEditSectionAvailable ? (
            <>
              <MenuTitle variant="blurred" text="EDIT" />

              {linkUpscale && (
                <MenuItem
                  w="170px"
                  iconId="Nav/Upscale/Outline"
                  iconH="12px"
                  internalLink={linkUpscale}
                  onClick={handleUpscaleClick}
                  text="Enhance"
                />
              )}
              {linkCanvas && (
                <MenuItem
                  w="170px"
                  iconId="Nav/Canvas/Outline"
                  iconH="14px"
                  internalLink={linkCanvas}
                  onClick={handleRetouchClick}
                  target="_blank"
                  text="Retouch"
                />
              )}
              {linkExpand && (
                <MenuItem
                  w="170px"
                  iconId="Nav/Expand/Outline"
                  iconH="14px"
                  internalLink={linkExpand}
                  onClick={handleExpandClick}
                  text="Expand"
                />
              )}
              {linkPromptEdit && (
                <MenuItem
                  w="170px"
                  iconId="Nav/Prompt/Outline"
                  iconH="14px"
                  internalLink={linkPromptEdit}
                  onClick={handlePromptEditClick}
                  text="Edit with Prompts"
                />
              )}

              <MenuDivider />
            </>
          ) : undefined}

          {isConvertSectionAvailable ? (
            <>
              <MenuTitle variant="blurred" text="CONVERT" />

              {linkConvertTo3D && (
                <MenuItem
                  w="170px"
                  iconId="Nav/3D/Outline"
                  iconH="15px"
                  internalLink={linkConvertTo3D}
                  onClick={handleConvertTo3DClick}
                  text="3D"
                />
              )}

              {linkConvertToVideo && (
                <MenuItem
                  w="170px"
                  iconId="Nav/Video/Outline"
                  iconH="11px"
                  internalLink={linkConvertToVideo}
                  onClick={handleConvertToVideoClick}
                  text="Video"
                />
              )}

              <MenuDivider />
            </>
          ) : undefined}

          {linkSearch && (
            <MenuItem
              w="170px"
              iconId="Ui/Search"
              iconH="12px"
              internalLink={linkSearch}
              onClick={handleSearchSimilarClick}
              text="Related Content"
            />
          )}
          <MenuItem
            w="170px"
            iconId="Ui/Download"
            iconH="12px"
            onClick={handleDownloadClick}
            isLoading={isDownloading}
            text="Download"
          />
          <MenuItem
            w="170px"
            iconId="Ui/Trash"
            iconH="12px"
            onClick={handleDeleteClick}
            isDisabled={!canDelete}
            isLoading={isDeleting}
            text="Delete"
          />

          <MenuItem
            w="170px"
            iconId="Ui/Dots"
            iconH="3px"
            onClick={handleMoreClick}
            text="More"
          />
        </MenuList>
      </Portal>
    </Menu>
  );
}
